// Shared types and utilities

export type Plan = "free" | "starter" | "pro" | "business" | "enterprise";
export type Role = "owner" | "admin" | "member" | "viewer";
export type DMARCPolicy = "none" | "quarantine" | "reject";
export type Alignment = "r" | "s";

export interface User {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
  avatarUrl?: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: Plan;
  domainLimit: number;
  messageLimit: number;
}

export interface OrganizationWithRole extends Organization {
  role: Role;
}

export interface Domain {
  id: string;
  organizationId: string;
  domain: string;
  verified: boolean;
  verificationToken: string;
  dmarcRecord?: string;
  spfRecord?: string;
  // User's configured DMARC policy settings
  configuredPolicy?: DMARCPolicy;
  configuredPct?: number;
  configuredSp?: DMARCPolicy;
  lastCheckedAt?: string;
  createdAt: string;
  updatedAt: string;
  // Report stats (populated on list)
  reportCount?: number;
  lastReportAt?: string;
}

export interface DMARCReport {
  id: string;
  organizationId: string;
  domainId: string;
  reportId: string;
  orgName: string;
  email?: string;
  dateRangeBegin: string;
  dateRangeEnd: string;
  policyDomain: string;
  policyAdkim: Alignment;
  policyAspf: Alignment;
  policyP: DMARCPolicy;
  policySp?: DMARCPolicy;
  policyPct: number;
  totalCount: number;
  passCount: number;
  failCount: number;
  createdAt: string;
}

export interface DMARCRecord {
  id: string;
  reportId: string;
  organizationId: string;
  sourceIp: string;
  count: number;
  disposition: DMARCPolicy;
  dkimDomain?: string;
  dkimResult?: string;
  dkimSelector?: string;
  spfDomain?: string;
  spfResult?: string;
  spfScope?: string;
  dkimAligned: boolean;
  spfAligned: boolean;
  dmarcPass: boolean;
  headerFrom?: string;
  envelopeFrom?: string;
  country?: string;
  asn?: number;
  asnOrg?: string;
  createdAt: string;
}

// Plan limits
export const PLAN_LIMITS: Record<Plan, { domains: number; messages: number }> = {
  free: { domains: 1, messages: 1_000 },
  starter: { domains: 5, messages: 100_000 },
  pro: { domains: 25, messages: 1_000_000 },
  business: { domains: 100, messages: 10_000_000 },
  enterprise: { domains: Infinity, messages: Infinity },
};

// Domain limits by plan (convenience alias)
export const DOMAIN_LIMITS: Record<Plan, number> = {
  free: 1,
  starter: 5,
  pro: 25,
  business: 100,
  enterprise: Infinity,
};

// Data retention periods by plan (in days)
export const RETENTION_DAYS: Record<Plan, number> = {
  free: 7,
  starter: 90,
  pro: 365,
  business: 365,
  enterprise: 365,
};
